import type { Product } from '../../catalog/domain-api'
import type { ProductSearchRow } from '../../catalog/domain-api';
import type { CartEvaluationResult } from '../../promotions/domain/promotionTypes';
import { DEFAULT_MONEY_SCALE, roundMoney, toMinorUnits, type MoneyScale } from '@shared/money';
import type { CartItem } from '../store/usePosStore';
import type { PosDraftPayment } from '../domain/posTypes';
import { computePosCheckoutTotals } from './posCheckoutTotals';

export const derivePosCheckoutTotals = (
  cart: CartItem[],
  paymentDrafts: PosDraftPayment[],
  evaluation?: CartEvaluationResult | null,
  extraDiscountTotal = 0,
  moneyScale: MoneyScale = DEFAULT_MONEY_SCALE,
) => {
  const base = evaluation
    ? (() => {
        const evaluatedByLineId = new Map(evaluation.lines.map((line) => [line.lineId, line]));
        const computed = computePosCheckoutTotals(
          cart.map((item) => {
            const evaluated = evaluatedByLineId.get(item.lineId);
            if (!evaluated) {
              return { price: item.price, quantity: item.quantity };
            }
            return {
              price: evaluated.unitPriceOriginal,
              quantity: evaluated.quantity,
              effectiveUnitPrice: evaluated.unitPrice,
              lineDiscountTotal: evaluated.lineDiscountTotal,
            };
          }),
          paymentDrafts,
          moneyScale,
        );
        const totalIngresado = computed.totalIngresado;
        const total = roundMoney(evaluation.total, moneyScale);
        const factor = 10 ** moneyScale.fractionDigits;
        const vueltoMinor = Math.max(
          0,
          toMinorUnits(totalIngresado, moneyScale) - toMinorUnits(total, moneyScale),
        );
        const saldoMinor = Math.max(
          0,
          toMinorUnits(total, moneyScale) - toMinorUnits(totalIngresado, moneyScale),
        );
        return {
          ...computed,
          subtotal: evaluation.subtotal,
          discountTotal: evaluation.discountTotal,
          total,
          totalIngresado,
          vuelto: vueltoMinor / factor,
          saldoPendiente: saldoMinor / factor,
        };
      })()
    : computePosCheckoutTotals(
        cart.map((item) => ({ price: item.price, quantity: item.quantity })),
        paymentDrafts,
        moneyScale,
      );
  const extra = Math.max(0, Number(extraDiscountTotal) || 0);
  if (extra <= 0) return base;
  const total = Math.max(0, roundMoney(base.total - extra, moneyScale));
  const discountTotal = roundMoney(base.discountTotal + extra, moneyScale);
  const totalIngresado = base.totalIngresado;
  const factor = 10 ** moneyScale.fractionDigits;
  const vueltoMinor = Math.max(
    0,
    toMinorUnits(totalIngresado, moneyScale) - toMinorUnits(total, moneyScale),
  );
  const saldoMinor = Math.max(
    0,
    toMinorUnits(total, moneyScale) - toMinorUnits(totalIngresado, moneyScale),
  );
  return {
    ...base,
    discountTotal,
    total,
    vuelto: vueltoMinor / factor,
    saldoPendiente: saldoMinor / factor,
  };
};

type PosStockProduct = ProductSearchRow | Product;

/** Cantidad vendible en POS: combos usan BOM (comboSellableQty), no stock del padre phantom. */
export function effectivePosSellableQuantity(product: PosStockProduct): number | null {
  if (product.itemType === 'service' || product.isMisc === true) return null;
  const isCombo = 'isCombo' in product && product.isCombo === true;
  if (isCombo) {
    const sell =
      'comboSellableQty' in product ? product.comboSellableQty : undefined;
    return typeof sell === 'number' ? sell : null;
  }
  const stockQuantity = 'stockQuantity' in product ? product.stockQuantity : undefined;
  return typeof stockQuantity === 'number' ? stockQuantity : null;
}

/**
 * Productos del catálogo en grilla incluyen columnas de stock; el lookup por código también las trae cuando hay sucursal.
 * Combos: misma regla que tienda online / catálogo — vendible = mínimo de componentes (`comboSellableQty`).
 */
export const isProductCardOutOfStock = (product: PosStockProduct): boolean => {
  if (product.itemType === 'service' || product.isMisc === true) return false;
  const isCombo = 'isCombo' in product && product.isCombo === true;
  if (isCombo) {
    const sell = effectivePosSellableQuantity(product);
    if (sell == null) return false;
    return sell <= 0;
  }
  const stockId = 'stockId' in product ? product.stockId : undefined;
  const stockQuantity = 'stockQuantity' in product ? product.stockQuantity : undefined;
  // Nunca cargaron stock (sin fila): no bloquear el tap. Qty 0 sí es sin stock.
  if (stockId == null && (stockQuantity == null || typeof stockQuantity !== 'number')) {
    return false;
  }
  return typeof stockQuantity !== 'number' || stockQuantity <= 0;
};

/** Códigos largos (EAN) en tarjeta POS: últimos dígitos para no saturar la vista. */
export const formatSkuForPosCard = (sku: string | null | undefined): string => {
  const t = String(sku ?? '').trim();
  if (!t) return 'Sin código';
  const digitsOnly = t.replace(/\D/g, '');
  if (digitsOnly.length >= 8) return `···${digitsOnly.slice(-4)}`;
  if (t.length > 14) return `···${t.slice(-4)}`;
  return t;
};
